import React from "react";
import {
  PlayIcon,
  PencilIcon,
  TrashIcon,
  DocumentTextIcon,
  PhotoIcon,
  DocumentIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";
import { Video, FileType } from "../types";
import { getImageUrl } from "../../../../services/api";

interface VideoCardProps {
  video: Video;
  isSelected: boolean;
  onSelect: (videoId: number) => void;
  onPlay: (video: Video) => void;
  onEdit: (video: Video) => void;
  onDelete: (videoId: number) => void;
  isActionLoading: boolean;
}

// Helper: Get icon for file type
const getFileTypeIcon = (fileType?: FileType) => {
  switch (fileType) {
    case "pdf":
      return <DocumentTextIcon className="h-6 w-6 lg:h-8 lg:w-8 text-white" />;
    case "document":
      return <DocumentIcon className="h-6 w-6 lg:h-8 lg:w-8 text-white" />;
    case "image":
      return <PhotoIcon className="h-6 w-6 lg:h-8 lg:w-8 text-white" />;
    case "video":
    default:
      return <PlayIcon className="h-6 w-6 lg:h-8 lg:w-8 text-white" />;
  }
};

// Helper: Get badge for file type
const getFileTypeBadge = (fileType?: FileType) => {
  switch (fileType) {
    case "pdf":
      return { label: "PDF", color: "bg-red-500" };
    case "document":
      return { label: "DOC", color: "bg-blue-500" };
    case "image":
      return { label: "IMG", color: "bg-green-500" };
    case "video":
      return { label: "VID", color: "bg-purple-500" };
    default:
      return { label: "FILE", color: "bg-gray-500" };
  }
};

// Helper: Format file size
const formatFileSize = (bytes?: number) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

const VideoCard: React.FC<VideoCardProps> = ({
  video,
  isSelected,
  onSelect,
  onPlay,
  onEdit,
  onDelete,
  isActionLoading,
}) => {
  const fileType = video.file_type || "video";
  const badge = getFileTypeBadge(fileType);
  const isVideo = fileType === "video";

  return (
    <div
      className={`bg-gradient-to-br from-white to-gray-50 overflow-hidden shadow-xl rounded-2xl hover:shadow-xl transition-all duration-300 group transform hover:-translate-y-1 ${
        isSelected ? "ring-2 ring-blue-500" : ""
      }`}
    >
      <div
        onClick={() => onPlay(video)}
        className="relative aspect-video bg-gradient-to-br from-gray-100 to-gray-200 cursor-pointer"
      >
        <div onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onSelect(video.id)}
            className="absolute top-2 left-2 z-10 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            aria-label={`Select ${video.title}`}
          />
        </div>
        {video.thumbnail_url ? (
          <img
            src={getImageUrl(video.thumbnail_url)}
            alt={video.title}
            className="w-full h-full object-cover bg-gray-100 overflow-hidden"
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center"
            style={{
              backgroundColor: video.thumbnail_color || "#3B82F6",
            }}
            title={`Color: ${video.thumbnail_color || "#3B82F6"}`}
          >
            {getFileTypeIcon(fileType)}
          </div>
        )}

        {/* File type badge */}
        <div
          className={`absolute top-2 right-2 ${badge.color} text-white text-[9px] font-bold px-1.5 py-0.5 rounded-md shadow-lg uppercase tracking-wider`}
        >
          {badge.label}
        </div>

        {/* Duration / Size badge */}
        <div className="absolute bottom-2 right-2 bg-black bg-opacity-80 text-white text-xs px-2 py-1 rounded-lg shadow-lg">
          {isVideo && video.duration > 0
            ? `${Math.floor(video.duration / 60)}:${(video.duration % 60).toString().padStart(2, "0")}`
            : formatFileSize(video.file_size) || "—"}
        </div>
      </div>
      <div className="p-2 lg:p-3">
        <h3
          className="text-xs lg:text-sm font-semibold text-gray-900 truncate mb-1"
          title={video.title}
        >
          {video.title}
        </h3>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            {isVideo ? (
              <span className="text-[10px] lg:text-xs text-gray-500 truncate font-medium">
                {video.views_count || 0} views
              </span>
            ) : (
              <span className="text-[10px] lg:text-xs text-gray-400 truncate">
                {video.original_filename || ""}
              </span>
            )}
          </div>
          <div className="flex items-center space-x-1 opacity-100 lg:opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onPlay(video);
              }}
              className="p-1 px-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-all duration-200"
              title={isVideo ? "ຫຼິ້ນວິດີໂອ" : "ດາວໂຫລດໄຟລ໌"}
            >
              {isVideo ? (
                <PlayIcon className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
              ) : (
                <ArrowDownTrayIcon className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
              )}
            </button>
            <button
              onClick={() => onEdit(video)}
              className="p-1 px-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-all duration-200"
              title="ແກ້ໄຂ"
            >
              <PencilIcon className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
            </button>
            <button
              onClick={() => onDelete(video.id)}
              disabled={isActionLoading}
              className="p-1 px-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-all duration-200 disabled:opacity-50"
              title="ລຶບ"
            >
              <TrashIcon className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoCard;
